import type { ReactNode } from 'react'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { OnboardingNavyShell } from '@/components/onboarding/NavyShell'

/**
 * Le cadre des écrans d'avant le paiement (numéro, entreprise), dans le même
 * moule que la mise en service : la question à gauche, ce que le client obtient
 * à droite, Retour et Continuer sur la même ligne en bas. Pas de compteur
 * d'étapes : deux écrans courts n'en ont pas besoin, et le vrai compteur
 * commence après le paiement. La déconnexion reste : rien n'est encore acheté.
 */
export function CadreAvant({
  titre,
  sousTitre,
  onRetour,
  onContinuer,
  continuer,
  continuerDesactive,
  enCours,
  apercu,
  children,
}: {
  titre: ReactNode
  sousTitre?: ReactNode
  onRetour?: () => void
  onContinuer: () => void
  /** Le libellé du bouton principal, « Continuer » par défaut. */
  continuer?: ReactNode
  continuerDesactive?: boolean
  enCours?: boolean
  /** Ce que le client obtient, à droite. Absent avant le paiement : on choisit, une seule colonne. */
  apercu?: ReactNode
  children: ReactNode
}) {
  const { t } = useTranslation()
  return (
    <OnboardingNavyShell panneau={apercu}>
      <main className="flex-grow flex items-center justify-center px-5 py-6 md:py-10">
        <form
          className="w-full max-w-[500px]"
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            if (!continuerDesactive && !enCours) onContinuer()
          }}
        >
          <div className="mb-8">
            <h1 className="text-[28px] md:text-[34px] font-semibold leading-[1.15] tracking-[-0.02em] text-balance">
              {titre}
            </h1>
            {sousTitre && (
              <p className="mt-3 text-[15px] leading-relaxed" style={{ color: '#8B9BB4' }}>
                {sousTitre}
              </p>
            )}
          </div>

          {children}

          <div
            className={`mt-14 pt-6 border-t border-white/[0.06] flex flex-col-reverse gap-3 sm:flex-row sm:items-center ${
              onRetour ? 'sm:justify-between' : 'sm:justify-end'
            }`}
          >
            {onRetour && (
              <button
                type="button"
                onClick={onRetour}
                className="h-11 sm:-ml-2 px-2 text-[14px] font-medium inline-flex items-center justify-center gap-1.5 rounded-lg hover:text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                style={{ color: '#8B9BB4' }}
              >
                <ArrowLeft className="w-4 h-4" />
                {t('onboarding.miseEnService.back')}
              </button>
            )}
            <button
              type="submit"
              disabled={continuerDesactive || enCours}
              className="w-full sm:w-auto h-11 px-7 rounded-xl text-[15px] font-semibold inline-flex items-center justify-center gap-2 transition-all hover:bg-white/90 active:scale-[0.98] disabled:opacity-35 disabled:cursor-not-allowed disabled:active:scale-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              style={{ backgroundColor: 'white', color: '#0A192F' }}
            >
              {enCours && <Loader2 className="w-4 h-4 animate-spin" />}
              {continuer ?? t('onboarding.miseEnService.continue')}
            </button>
          </div>
        </form>
      </main>
    </OnboardingNavyShell>
  )
}

/** Champ texte sur fond sombre, le même que celui des invitations. */
export const CHAMP_SOMBRE =
  'w-full h-12 px-4 rounded-xl outline-none transition-all text-[15px] bg-white/[0.04] border border-white/[0.14] text-white placeholder:text-white/35 focus:border-white/40'

export function Etiquette({ htmlFor, children }: { htmlFor?: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="block mb-2 text-sm font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>
      {children}
    </label>
  )
}

export function Erreur({ children }: { children: ReactNode }) {
  return (
    <p className="mt-2 text-[13px]" style={{ color: '#FCA5A5' }}>
      {children}
    </p>
  )
}
